/**
 * SOLID Review
 * S — Strong. One concern: the legal REST surface as the `ClientLegalServer` port speaks it — the
 *     documents, the account's consent status and consents, the GDPR export and deletion, and (FE4b) the
 *     cookie-consent audit record.
 * O — Good. A new legal endpoint is one more method over the same client.
 * L — Strong. Implements the port interface exactly.
 * I — Good. The port's seven ops, a matched set.
 * D — Good. The REST client port, the wire contract, the token holder.
 */
import { rest as restContract } from '@ml3d/contract';
import type { IContextRequest } from '../../domain/ports/schemas/ContextRequest';
import type { IPayloadRecordConsent, ICommandDeleteAccount, IPayloadLegalDocuments, IPayloadDataExport } from '../../domain/ports/schemas/LegalMoves';
import type { IPortClientLegalServer } from '../../application/ports/contracts';
import type { IPortRestApiClient } from '../contract/ports';
import { restCall } from '../contract/restResult';
import { getOrCreateConsentId } from '../../shared/consentId';
import type { IPayloadCookieConsent } from '../../domain/ports/schemas/ConsentMoves';

const legalContract = restContract.legal;

/** Named + annotated per the house rule: an object literal at one of OUR parameters is never inline. */
interface IConfigLegalRequest {
  readonly method: string;
  readonly body?: unknown;
  readonly jwt?: string | null;
}

interface ICommandRecordConsentBody {
  readonly documentType: string;
  readonly documentVersion: string;
}

interface ICommandDeleteAccountBody {
  readonly code: string;
}

/** The cookie-consent audit body: the client's decision plus this browser's consent id. */
interface ICommandCookieConsentBody extends IPayloadCookieConsent {
  readonly consentId: string;
}

/**
 * THE LEGAL GATEWAY (frontend strict-ports plan, FE4a, 2026-10-04) — `LegalApiAdapter`, which the Framework
 * held as `legalAdapter` and threw from, now the port's implementation, answering results. The calls and
 * their parsing are unchanged: routed through `restLegalApiSocket` (2026-08-30), each answer parsed against
 * @ml3d/contract except the GDPR export, a deliberate passthrough — a subject-access export is whatever the
 * player holds, and the contract declares no schema for it.
 *
 * The token comes from a thunk — the token holder's read since FE3 — so a player who signs in after the
 * page loaded is not anonymous to their own consent, export and deletion.
 */
export class ClientLegalServerAdapter implements IPortClientLegalServer {
  constructor(
    private readonly getToken: () => string | null,
    private readonly client: IPortRestApiClient,
  ) {}

  async documents(_ctx: IContextRequest) {
    const request: IConfigLegalRequest = { method: 'GET' };
    return restCall(async (): Promise<IPayloadLegalDocuments> => {
      const documents = await this.client.request('/api/legal/documents', request, (d) => legalContract.LegalDocumentsResponse.parse(d));
      return { documents: documents.map((doc) => ({ type: doc.type, version: doc.version, title: doc.title, content: doc.content })) };
    });
  }

  async consentStatus(_ctx: IContextRequest) {
    const request: IConfigLegalRequest = { method: 'GET', jwt: this.getToken() };
    return restCall(() => this.client.request('/api/legal/consent/status', request, (d) => legalContract.ConsentStatusResponse.parse(d)));
  }

  async recordConsent(_ctx: IContextRequest, consent: IPayloadRecordConsent) {
    const body: ICommandRecordConsentBody = { documentType: consent.documentType, documentVersion: consent.documentVersion };
    const request: IConfigLegalRequest = { method: 'POST', body, jwt: this.getToken() };
    return restCall(async () => { await this.client.request('/api/legal/consent', request, () => undefined); });
  }

  async exportMyData(_ctx: IContextRequest) {
    const token = this.getToken();
    if (!token) return { success: false as const, error: { code: 'NOT_SIGNED_IN', message: 'Not authenticated.' } };
    const request: IConfigLegalRequest = { method: 'GET', jwt: token };
    return restCall(async (): Promise<IPayloadDataExport> => ({ data: await this.client.request('/api/legal/me/export', request, (d) => d) }));
  }

  async requestAccountDeletionCode(_ctx: IContextRequest) {
    const request: IConfigLegalRequest = { method: 'POST', jwt: this.getToken() };
    return restCall(async () => { await this.client.request('/api/legal/me/delete-request', request, () => undefined); });
  }

  async deleteAccount(_ctx: IContextRequest, command: ICommandDeleteAccount) {
    const body: ICommandDeleteAccountBody = { code: command.code };
    const request: IConfigLegalRequest = { method: 'DELETE', body, jwt: this.getToken() };
    return restCall(async () => { await this.client.request('/api/legal/me', request, () => undefined); });
  }

  /**
   * The cookie-consent audit record (FE4b — `ConsentApiAdapter`, moved): the decision, this browser's
   * consent id (minted once and kept), and the token when one is held — consent is recorded for anonymous
   * visitors too.
   */
  async recordCookieConsent(_ctx: IContextRequest, consent: IPayloadCookieConsent) {
    const body: ICommandCookieConsentBody = { consentId: getOrCreateConsentId(), ...consent };
    const request: IConfigLegalRequest = { method: 'POST', body, jwt: this.getToken() };
    return restCall(async () => {
      await this.client.request('/api/legal/cookie-consent', request, (d) => legalContract.CookieConsentResponse.parse(d));
    });
  }
}
